import { useState } from "react";
import { Alert, Button, ProgressiveListFooter, Select } from "@playbookai/ui";
import {
  saveDefaults,
  type ExecutionProfile,
  type ModelDefaults,
} from "../../../api/admin-ai-profiles-api.js";

const APP_LABELS: Record<string, string> = {
  home: "Home",
  solve: "SOLVE",
  hoshin: "Hoshin",
  vsm: "VSM",
};
export function DefaultsEditor({
  defaults,
  profiles,
  hasMoreProfiles = false,
  loadingMoreProfiles = false,
  profileLoadError,
  onLoadMoreProfiles = () => {},
  onRetryProfiles = () => {},
  onSaved,
}: {
  defaults: ModelDefaults;
  profiles: ExecutionProfile[];
  hasMoreProfiles?: boolean;
  loadingMoreProfiles?: boolean;
  profileLoadError?: string | undefined;
  onLoadMoreProfiles?: () => void;
  onRetryProfiles?: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = profiles
    .filter((profile) => profile.available)
    .map((profile) => ({ value: profile.id, label: profile.display_name }));
  const withCurrent = (profileId: string | null) => {
    if (!profileId || options.some((option) => option.value === profileId)) {
      return options;
    }
    const current = profiles.find((profile) => profile.id === profileId);
    return [
      {
        value: profileId,
        label: `${current?.display_name ?? profileId} (unavailable)`,
      },
      ...options,
    ];
  };
  // Our save response can arrive before the refetched props. Only a newer
  // server version represents another writer changing this draft.
  const stale = defaults.version > draft.version;
  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      setDraft(await saveDefaults(draft));
      setSaved(true);
      onSaved();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not save defaults.",
      );
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="grid min-w-0 gap-4 [&>*]:min-w-0">
      {error && (
        <Alert tone="critical" title="Couldn't save the defaults">
          {error}
        </Alert>
      )}
      {stale && (
        <Alert tone="info">
          Defaults changed. Reload the saved values before editing again.{" "}
          <Button
            variant="secondary"
            onClick={() => {
              setDraft(defaults);
              setSaved(false);
              setError(null);
            }}
          >
            Reload defaults
          </Button>
        </Alert>
      )}
      <Select
        label="Platform default"
        value={draft.platform_profile_id ?? ""}
        disabled={saving || stale}
        onChange={(value) => {
          setSaved(false);
          setDraft((previous) => ({
            ...previous,
            platform_profile_id: value || null,
          }));
        }}
        options={[
          { value: "", label: "Not configured" },
          ...withCurrent(draft.platform_profile_id),
        ]}
      />
      {Array.from(
        new Set([...Object.keys(APP_LABELS), ...Object.keys(draft.apps)]),
      ).map((app) => (
        <Select
          key={app}
          label={`${APP_LABELS[app] ?? app} default`}
          hint={
            !draft.apps[app]
              ? `Inherited profile: ${profiles.find((profile) => profile.id === draft.platform_profile_id)?.display_name ?? "Not configured"}`
              : ""
          }
          value={draft.apps[app] ?? ""}
          disabled={saving || stale}
          onChange={(value) => {
            setSaved(false);
            setDraft((previous) => ({
              ...previous,
              apps: { ...previous.apps, [app]: value || null },
            }));
          }}
          options={[
            { value: "", label: "Inherit platform default" },
            ...withCurrent(draft.apps[app] ?? null),
          ]}
        />
      ))}
      <ProgressiveListFooter
        loadedCount={profiles.length}
        itemLabel="profiles"
        singularItemLabel="profile"
        loadMoreLabel="Load more profiles"
        hasMore={hasMoreProfiles}
        busy={loadingMoreProfiles}
        error={profileLoadError}
        onLoadMore={onLoadMoreProfiles}
        onRetry={onRetryProfiles}
      />
      <p className="text-sm text-pb-text-muted">
        Members can choose an enabled profile within each app. Group
        conversations and background jobs use the app default. An app with no
        effective default cannot start AI runs.
      </p>
      {saved && !stale && (
        <Alert tone="healthy">
          <span role="status">Defaults saved.</span>
        </Alert>
      )}
      <div>
        <Button
          variant="primary"
          disabled={saving || stale}
          onClick={() => {
            void save();
          }}
        >
          {saving ? "Saving…" : "Save defaults"}
        </Button>
      </div>
    </div>
  );
}
